// @flow
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import {
  Banner,
  Box,
  LinearProgress,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import messages from './messages';
import FindingsList from './FindingsList';
import { partitionFindings } from './utils';
import type { NaurFinding } from '../../services/naurService';

type Props = {
  open: boolean,
  intl: Object,
  findings: ?Array<NaurFinding>,
  alreadyInitialized: boolean,
  isPreflightLoading: boolean,
  hasPreflightError: boolean,
  isSubmitting: boolean,
  failureMessage: ?string,
  onClose: () => void,
  onConfirm: () => void,
};

const ConfirmationModal = ({
  open,
  intl,
  findings,
  alreadyInitialized,
  isPreflightLoading,
  hasPreflightError,
  isSubmitting,
  failureMessage,
  onClose,
  onConfirm,
}: Props) => {
  const { blockers } = partitionFindings(findings);

  // A preflight that did not answer is not evidence that the run is safe, so it
  // disables Confirm just as a blocker does.
  const confirmDisabled =
    isPreflightLoading ||
    isSubmitting ||
    hasPreflightError ||
    alreadyInitialized ||
    blockers.length > 0;

  const actionsProps = [
    {
      children: intl.formatMessage(messages.cancel),
      onClick: onClose,
      disabled: isSubmitting,
    },
    {
      children: intl.formatMessage(
        isSubmitting ? messages.confirming : messages.confirm,
      ),
      onClick: onConfirm,
      disabled: confirmDisabled,
      submitButton: true,
    },
  ];

  return (
    <Modal
      open={open}
      title={intl.formatMessage(messages.title)}
      onClose={isSubmitting ? undefined : onClose}
      noCloseIcon={isSubmitting}
      status="warning"
      actionsProps={actionsProps}
    >
      <Typography variant="labelLarge" display="block" marginBottom={1.5}>
        <FormattedMessage {...messages.onceOnly} />
      </Typography>
      <Banner
        status="warning"
        message={intl.formatMessage(messages.readyToContinue)}
      />
      <Typography
        variant="bodyMedium"
        color="text.secondary"
        display="block"
        marginTop={1.5}
      >
        <FormattedMessage {...messages.noLongerAvailable} />
      </Typography>
      <Typography
        variant="bodyMedium"
        color="text.secondary"
        display="block"
        marginTop={1.5}
      >
        <FormattedMessage {...messages.futureAutomatic} />
      </Typography>

      {isPreflightLoading && (
        <>
          <Typography
            variant="bodySmall"
            color="text.secondary"
            display="block"
            marginTop={2}
          >
            <FormattedMessage {...messages.checkingPrerequisites} />
          </Typography>
          <LinearProgress />
        </>
      )}

      {hasPreflightError && (
        <Banner
          status="error"
          message={intl.formatMessage(messages.preflightError)}
          sx={{ marginTop: 2 }}
        />
      )}

      {alreadyInitialized && (
        <Banner
          status="info"
          message={intl.formatMessage(messages.alreadyInitialized)}
          sx={{ marginTop: 2 }}
        />
      )}

      {failureMessage ? (
        <Banner status="error" message={failureMessage} sx={{ marginTop: 2 }} />
      ) : null}

      <Box
        data-testid="findings-scroll-region"
        sx={{ marginTop: 2, maxHeight: 280, overflowY: 'auto' }}
      >
        <FindingsList findings={findings} />
      </Box>
    </Modal>
  );
};

export default ConfirmationModal;
